import { Injectable, Injector } from '@angular/core';
import { Form } from '@angular/forms';
import { BaseService } from 'src/app/common/base/base.service';
import { FormHistoryServicesHelper } from '../../form-history-services/form-history-services-helper';
import { LogForm } from '../../form-history-services/form-history.service';
import { IFormHistoryService } from '../../form-history-services/i-form-history-service';

@Injectable({
  providedIn: 'root'
})
export class FormHistoryRsfService extends BaseService<any, number> implements IFormHistoryService {

  amendmentSectionNumbers = [7];
  helper = FormHistoryServicesHelper;

  constructor(
    protected override injector: Injector,
  ) {
    super(injector, '');
  }

  /**
   * Builds RSF amendment history and preserves Minor Amendment metadata from the current form.
   * @param form Original RSF body from the first history snapshot.
   * @param logForm History payload containing signatures and amendment updates.
   * @param finalForm Current RSF containing persisted amendment classifications.
   */
  async processForm(form: any, logForm: LogForm, finalForm: any) {

    form.createdByName = this.users?.find(u => u.id === form.createdBy)?.name;
    form.requesterName = this.users?.find(u => u.id === form.requesterId)?.name;

    // History JSON can omit the nested document details required to display ASCC Link labels.
    this.restoreRelatedDocuments(form, form, finalForm);

    logForm.updates = this.processUpdates(logForm, form, finalForm);
    logForm.originalBody = form;
    return logForm;
  }

  /**
   * Converts RSF update snapshots into amendment tabs and marks Minor Amendments.
   * @param logForm History payload containing update snapshots.
   * @param form Original RSF body used as the first comparison baseline.
   * @param finalForm Current RSF containing persisted amendment classifications.
   */
  processUpdates(logForm: LogForm, form: Form, finalForm: any) {
    // ---- Amendments list ----
    const amendmentsSet = new Set<number>(this.helper.getAmendmentList(logForm.signatures ?? []));
    const minorAmendmentIDs = new Set<number>(finalForm?.rsfAmendments
      ?.filter((amendment: any) => amendment.isMinorAmendment && !!amendment.wfAmendmentID)
      .map((amendment: any) => amendment.wfAmendmentID) ?? []);
    const amendmentNumbers = new Map<number, number>();
    finalForm?.wfTable?.wfTableLocal?.wfSectionLocals
      ?.flatMap((section: any) => section.wfSignatures ?? [])
      .forEach((signature: any) => {
        if (signature.wfAmendmentID && signature.amendmentNumber && !amendmentNumbers.has(signature.wfAmendmentID))
          amendmentNumbers.set(signature.wfAmendmentID, signature.amendmentNumber);
      });

    // ---- Updates ----
    const updates = logForm?.updates ?? [];

    updates?.forEach(update => {
      // Parse old body
      if (update.prevBodyJson) {
        const prev = this.helper.createBody(update.prevBodyJson, this.users);
        const current = this.helper.createBody(update.currentBodyJson ?? '', this.users);
        // Reuse populated document details from the original/current RSF without changing the snapshot values.
        this.restoreRelatedDocuments(prev, form, finalForm);
        this.restoreRelatedDocuments(current, form, finalForm);
        current.active = true;
        current.rsf = prev;
        prev.rsfAmendments = [current];
        update.prevBody = prev;
        update.currentBody = prev;
        // Keep completed Minor Amendments identifiable after they return the RSF to Active.
        update.isMinorAmendment = minorAmendmentIDs.has(update.amendmentId ?? 0);
        if (update.amendmentId && amendmentsSet) {
          // Prefer the workflow's displayed amendment number instead of recounting canceled workflow IDs.
          const amendmentNumber = amendmentNumbers.get(update.amendmentId);
          update.header = amendmentNumber
            ? `<span class="yellow">Amendment ${amendmentNumber}</span>`
            : this.helper.getLogHeadText(update.amendmentId, amendmentsSet);
        }
      }
    });

    return updates;
  }

  /**
   * Restores nested related-document display details omitted from serialized RSF history snapshots.
   * @param body Historical RSF or amendment snapshot to enrich in place.
   * @param sources Populated RSF responses used to resolve related documents by their stable ID.
   */
  private restoreRelatedDocuments(body: any, ...sources: any[]): void {
    const sourceDocuments = sources
      .flatMap(source => [
        ...(source?.rsfRelatedDocuments ?? []),
        ...(source?.rsfAmendments?.flatMap((amendment: any) => amendment?.rsfRelatedDocuments ?? []) ?? [])
      ])
      .filter(document => document?.relatedDocument);
    const documentsById = new Map<number, any>(sourceDocuments.map(document => [document.relatedDocumentID, document.relatedDocument] as [number, any]));

    body?.rsfRelatedDocuments?.forEach((document: any) => {
      document.relatedDocument = document.relatedDocument ?? documentsById.get(document.relatedDocumentID);
    });
  }
}
